import { ReactNode, useMemo } from "react";
import { Block, BLOCKS, Inline, INLINES } from "@contentful/rich-text-types";
import { ResultOf } from "gql.tada";
import { Options } from "@contentful/rich-text-react-renderer";
import { Link } from "@components/shared/core/Link";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Button } from "@components/shared/core/Button";
import { MediaItem, MediaItemType } from "@components/shared/MediaItem";
import merge from "lodash/merge";
import { DeepPartial } from "../types/DeepPartial";
import { IconGridFragment } from "@queries/fragments/IconGridFragment";
import { ArticleSideBySideImageFragment } from "@queries/fragments/ArticleSideBySideImageFragment";
import { AccessibleMediaFragment } from "@queries/fragments/AccessibleMediaFragment";

type RichTextEntry<T = object> = {
  sys: {
    id: string;
    __typename?: string;
  };
} & T;

export type RichTextLinks = {
  assets: {
    block: Array<RichTextEntry<{
      url: string | null;
      contentType: string | null;
    }> | null>;
  };
  entries: {
    inline: Array<RichTextEntry | RichTextEntry<{ body?: { json: unknown } | null }> | null>;
    hyperlink: Array<RichTextEntry<{ slug?: string | null }> | null>;
    // Link entries: slug is now a direct field (denormalized from reference),
    // not nested under pageReference. Also includes text, isExternal, url
    // from the updated LinkFragment.
    block: Array<RichTextEntry<{
      text?: string | null;
      isExternal?: boolean;
      url?: string;
      slug?: string | null;
    }> | null>;
  };
};

export const buildRichTextMaps = (
  links?: DeepPartial<RichTextLinks>
): RichTextMaps => {
  const assetMap = new Map();
  const entryMap = new Map();

  if (links) {
    links.assets?.block?.forEach(
      (asset) => asset && assetMap.set(asset.sys?.id, asset)
    );

    (["block", "hyperlink", "inline"] as const).forEach((key) => {
      links.entries?.[key]?.forEach(
        (entry) => entry && entryMap.set(entry.sys?.id, entry)
      );
    });
  }

  return { assetMap, entryMap };
};

type RichTextMaps = {
  assetMap: Map<unknown, unknown>;
  entryMap: Map<unknown, unknown>;
};

type Override = (
  node: Block | Inline,
  children: ReactNode,
  maps: RichTextMaps
) => JSX.Element | null;

export type RichTextRenderOverrides = {
  blocks?: {
    embeddedEntry?: {
      Link?: Override;
      IconGrid?: Override;
    };
  };
};

export const useRichTextRenderOptions = (
  links?: DeepPartial<RichTextLinks>,
  {
    options,
    overrides,
  }: { options?: Options; overrides?: RichTextRenderOverrides } = {}
): Options =>
  useMemo(() => {
    const { assetMap, entryMap } = buildRichTextMaps(links);

    const mergedOptions = merge(
      {
        renderText: (text) =>
          text.split("\n").flatMap((text, i) => [i > 0 && <br />, text]),
        renderNode: {
          [BLOCKS.HEADING_1]: (_, children) => (
            <h2 className="text-heading-1 [*+&]:mt-9">{children}</h2>
          ),
          [BLOCKS.HEADING_2]: (_, children) => (
            <h2 className="text-heading-2 [*+&]:mt-8">{children}</h2>
          ),
          [BLOCKS.HEADING_3]: (_, children) => (
            <h3 className="text-heading-3 [*+&]:mt-6">{children}</h3>
          ),
          [BLOCKS.HEADING_4]: (_, children) => (
            <h4 className="text-heading-4 [*+&]:mt-5">{children}</h4>
          ),
          [BLOCKS.HEADING_5]: (_, children) => (
            <h5 className="text-heading-5 [*+&]:mt-4">{children}</h5>
          ),
          [BLOCKS.HEADING_6]: (_, children) => (
            <h6 className="text-heading-6 [*+&]:mt-2">{children}</h6>
          ),
          [BLOCKS.PARAGRAPH]: (_, children) => (
            <p className="[*+&]:mt-5">{children}</p>
          ),
          [INLINES.HYPERLINK]: (node, children) => (
            <Link className="underline" href={node.data.uri}>
              {children}
            </Link>
          ),
          [INLINES.ENTRY_HYPERLINK]: (node, children) => {
            const embeddedEntry = entryMap.get(node?.data?.target?.sys?.id) as
              | { __typename: string; slug?: string | null }
              | null
              | undefined;

            if (!embeddedEntry) return <>{children}</>;

            const slug = embeddedEntry.slug ?? null;
            const isArticle = embeddedEntry.__typename === "ArticlePage";
            const href = slug
              ? isArticle
                ? `?articleModal=${slug}`
                : `/${slug}`
              : "#";

            return (
              <Link href={href} className="underline">
                {children}
              </Link>
            );
          },
          [INLINES.EMBEDDED_ENTRY]: (node) => {
            interface LinkWithType extends ResultOf<typeof LinkFragment> {
              __typename: string;
            }

            const embeddedEntry = entryMap.get(
              node.data.target.sys.id
            ) as LinkWithType;

            if (embeddedEntry?.__typename === "Link") {
              return (
                <Link link={embeddedEntry} className="underline">
                  {embeddedEntry.text}
                </Link>
              );
            }
            return null;
          },
          [BLOCKS.LIST_ITEM]: (_, children) => (
            <li className="ml-6">{children}</li>
          ),
          [BLOCKS.OL_LIST]: (_, children) => (
            <ol className="[&>*]:list-decimal [&>*]:list-outside">{children}</ol>
          ),
          [BLOCKS.UL_LIST]: (_, children) => (
            <ul className="[&>*]:list-disc [&>*]:list-outside">{children}</ul>
          ),
          [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
            interface LinkWithType extends ResultOf<typeof LinkFragment> {
              __typename: "Link";
            }
            interface IconGridWithType
              extends ResultOf<typeof IconGridFragment> {
              __typename: "IconGrid";
            }

            interface SideBySideMediaWithType
              extends ResultOf<typeof ArticleSideBySideImageFragment> {
              __typename: "SideBySideMedia";
            }

            const embeddedEntry = entryMap.get(node.data.target.sys.id) as
              | LinkWithType
              | IconGridWithType
              | SideBySideMediaWithType
              | undefined;

            if (!embeddedEntry) return null;

            if (embeddedEntry.__typename === "Link") {
              if (overrides?.blocks?.embeddedEntry?.Link) {
                return overrides.blocks.embeddedEntry.Link(node, children, {
                  assetMap,
                  entryMap,
                });
              }

              return (
                <Button asChild variant="light-on-blur" className="mt-[38px]">
                  <Link link={embeddedEntry}>{embeddedEntry.text}</Link>
                </Button>
              );
            }

            if (embeddedEntry.__typename === "IconGrid") {
              if (overrides?.blocks?.embeddedEntry?.IconGrid) {
                return overrides.blocks.embeddedEntry.IconGrid(node, children, {
                  assetMap,
                  entryMap,
                });
              }

              return (
                <div className="grid grid-cols-2 dsk:grid-cols-4">
                  {embeddedEntry.iconsCollection?.items?.map((icon) => (
                    <div
                      key={icon?._id}
                      className="w-[123px] dsk:w-[158px] aspect-square flex justify-center items-center"
                    >
                      {icon?.asset && (
                        <MediaItem
                          item={icon.asset}
                          className="w-full h-auto"
                          width={160}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            }

            if (embeddedEntry.__typename === "SideBySideMedia") {
              const items =
                embeddedEntry.mediaCollection?.items as unknown as ReadonlyArray<
                  ResultOf<typeof AccessibleMediaFragment> | null
                > | undefined;
              return (
                <div className="[*+&]:mt-20 flex flex-col dsk:flex-row gap-10 justify-between items-center">
                  {items?.map((item) => (
                    <div key={item?.sys?.id}>
                      {item?.media && (
                        <MediaItem
                          className="rounded-lg max-w-full object-contain w-[500px] aspect-video"
                          item={item.media}
                          alt={item.altText ?? undefined}
                          width={500}
                        />
                      )}
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          },
          [BLOCKS.EMBEDDED_ASSET]: (node) => {
            const asset = assetMap.get(
              node.data.target.sys.id
            ) as MediaItemType;
            return (
              <MediaItem
                item={asset}
                key={node.data.target.sys.id}
                className="[*+&]:mt-20 mb-20 rounded-xl"
                width={1000}
              />
            );
          },
          [BLOCKS.TABLE]: (_, children) => (
            <div className="[*+&]:mt-8 mb-8 overflow-x-auto border border-brand-black rounded-xl">
              <table className="w-full">
                <tbody>
                  {children}
                </tbody>
              </table>
            </div>
          ),
          [BLOCKS.TABLE_ROW]: (_, children) => <tr>{children}</tr>,
          [BLOCKS.TABLE_HEADER_CELL]: (_, children) => (
            <th className="bg-brand-cream text-brand-black text-left p-4 pt-6 border border-brand-black font-bold [&_*]:font-bold [&_*]:!text-brand-black [&>p]:text-heading-4 [&>p]:text-[22px]">
              {children}
            </th>
          ),
          [BLOCKS.TABLE_CELL]: (_, children) => (
            <td className="p-6 border border-brand-black bg-white font-normal text-left [&>p]:font-normal">
              {children}
            </td>
          ),
          ...options?.renderNode,
        },
      } satisfies Options,
      options
    );

    return mergedOptions;
  }, [links, options, overrides?.blocks?.embeddedEntry]);